import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useI18n } from "@/lib/i18n.jsx";

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) return toast.error(t("password_min"));
    if (pwd !== pwd2) return toast.error(t("passwords_dont_match"));
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: pwd,
      });
      setDone(true);
      toast.success(t("reset_success"));
      setTimeout(() => navigate("/auth"), 1800);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("reset_invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen w-full flex items-center justify-center bg-[#070707] text-white px-6 relative">
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg,#D92D20 0%,#D92D20 38%,#000 38%,#000 62%,#D92D20 62%,#D92D20 100%)" }} />
      <div className="w-full max-w-md border border-white/10 bg-white/[0.02] p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/intercar-logo.png" alt="" className="w-10 h-10 object-contain" />
          <span className="font-display font-bold tracking-[0.3em] text-[11px] text-white/60">INTERCAR · MANAGER</span>
        </div>

        {!token ? (
          <div>
            <h1 className="font-display font-black uppercase text-2xl tracking-tight mb-3">{t("reset_invalid")}</h1>
            <button
              data-testid="reset-go-login"
              onClick={() => navigate("/auth")}
              className="mt-4 w-full h-11 border border-white/15 hover:border-white/40 text-[11px] uppercase tracking-[0.3em] font-display font-bold text-white/75 transition-all"
            >
              {t("back_to_login")}
            </button>
          </div>
        ) : done ? (
          <div>
            <h1 className="font-display font-black uppercase text-2xl tracking-tight mb-3 text-success">✓ {t("reset_success")}</h1>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black uppercase text-3xl tracking-tight">{t("reset_title")}</h1>
            <p className="mt-2 text-white/50 text-sm">{t("reset_hint")}</p>
            <div className="mt-4 mb-8 w-12 h-[2px] bg-[#D92D20]" />

            <form onSubmit={submit} className="space-y-5">
              <label className="block">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display font-bold uppercase tracking-[0.25em] text-[10px] text-white/45">
                    {t("new_password_label")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    {showPwd ? t("hide") : t("show")}
                  </button>
                </div>
                <input
                  data-testid="reset-new-password"
                  type={showPwd ? "text" : "password"}
                  required
                  value={pwd}
                  autoComplete="new-password"
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D92D20] focus:outline-none px-1 h-11 text-base text-white transition-colors"
                />
              </label>
              <label className="block">
                <span className="block mb-2 font-display font-bold uppercase tracking-[0.25em] text-[10px] text-white/45">
                  {t("confirm_password_label")}
                </span>
                <input
                  data-testid="reset-confirm-password"
                  type={showPwd ? "text" : "password"}
                  required
                  value={pwd2}
                  autoComplete="new-password"
                  onChange={(e) => setPwd2(e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D92D20] focus:outline-none px-1 h-11 text-base text-white transition-colors"
                />
              </label>

              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading}
                className="w-full mt-2 h-12 font-display font-bold uppercase text-[12px] tracking-[0.32em] disabled:opacity-60 transition-all bg-[#D92D20] hover:bg-[#B42318]"
              >
                {loading ? "…" : t("reset_submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
